import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  Avatar,
  Grid,
  Chip,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import EditIcon from "@mui/icons-material/Edit";
import Helper from "../../common/Helper";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useTheme } from "@mui/styles";
import TitleTooltip from "../../sharedComponents/Tooltip/TitleTooltip";

const ribbonBgColor = "#A5D6A7";

const Ribbon = styled(Box)(({ theme, bgcolor }) => ({
  position: "absolute",
  top: -8,
  right: 16,
  backgroundColor: bgcolor,
  color: "#1d428a",
  fontWeight: "bold",
  fontSize: 12,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(0.6),
  paddingBottom: theme.spacing(0.6),
  borderRadius: "0 8px 8px 0",
  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
  userSelect: "none",
  zIndex: 10,
  whiteSpace: "nowrap",
}));

const UserCard = ({ user, roles, onEditUser }) => {
  const theme = useTheme();
  const {
    userId,
    firstName,
    lastName,
    email,
    active: isActive,
    teamName,
  } = user;

  const remainingRoles = roles
    .slice(2)
    .map(
      (role) =>
        `${role.roleName}${role.leagueName ? ` - ${role.leagueName}` : ""}`
    )
    .join(", ");

  return (
    <Card
      sx={{
        width: "100%",
        boxShadow: 3,
        borderRadius: 2,
        bgcolor: "white",
        p: 2,
        position: "relative",
        overflow: "visible",
      }}
    >
      {/* Top-right ribbon */}
      {teamName && <Ribbon bgcolor={ribbonBgColor}>{teamName}</Ribbon>}

      <Grid container alignItems="center" spacing={2}>
        <Grid
          item
          xs={10}
          sm={11}
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Avatar
            sx={{
              bgcolor: "#1d428a",
              width: 50,
              height: 50,
              fontSize: 16,
              color: theme.palette.getContrastText("#0c2c75"),
            }}
          >
            {firstName ? (
              Helper.GetNameInitials(`${lastName} ${firstName}`)
            ) : (
              <AccountCircleIcon />
            )}
          </Avatar>
          <Box sx={{ ml: 2, overflow: "hidden", textOverflow: "ellipsis" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
              <Typography
                color="primary"
                fontWeight="bold"
                sx={{
                  mr: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {`${lastName} ${firstName}`}
              </Typography>
              <Box
                sx={{
                  width: 15,
                  height: 15,
                  borderRadius: "50%",
                  bgcolor: isActive ? "green" : "red",
                }}
              />
            </Box>
            <Typography
              variant="body2"
              fontWeight={500}
              color="text.secondary"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {email}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={2} sm={1}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              height: "100%",
            }}
          >
            <IconButton
              onClick={() => onEditUser(userId)}
              size="small"
              sx={{
                color: "primary.main",
                bgcolor: "#fff",
                borderRadius: "50%",
                boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                "&:hover": {
                  bgcolor: "primary.light",
                  color: "#fff",
                },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        </Grid>
      </Grid>

      {/* Row 2: Role Chips */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mt: 2,
          justifyContent: "flex-start",
        }}
      >
        {roles.slice(0, 2).map((role, index) => (
          <Chip
            key={index}
            label={`${role.roleName}${
              role.leagueName ? ` - ${role.leagueName}` : ""
            }`}
            variant="outlined"
            sx={{
              fontWeight: "bold",
              bgcolor: "#f0f0f0",
              color: "primary.main",
              height: 24,
              fontSize: "0.75rem",
              px: 1,
              "& .MuiChip-label": {
                paddingLeft: 0.5,
                paddingRight: 0.5,
                fontSize: "0.75rem",
              },
            }}
          />
        ))}

        {/* Tooltip showing the remaining roles */}
        {remainingRoles && (
          <TitleTooltip title={remainingRoles} arrow>
            <Chip
              label={`+${roles.length - 2} more`}
              variant="outlined"
              sx={{
                fontWeight: "bold",
                bgcolor: "#f0f0f0",
                color: "primary.main",
                height: 24,
                fontSize: "0.75rem",
                px: 1,
                "& .MuiChip-label": {
                  paddingLeft: 0.5,
                  paddingRight: 0.5,
                  fontSize: "0.75rem",
                },
              }}
            />
          </TitleTooltip>
        )}
      </Box>
    </Card>
  );
};

export default UserCard;
